import type { ReactNode } from 'react';

/**
 * The colour vocabulary the whole tool shares.
 *
 * `teal` is the brand and means "this is the thing working"; the rest are the ordinary semantic
 * signals. Job states keep the meanings they had under the dark theme (waiting is cool, active is
 * green, delayed is amber, failed is red) — only the values moved to light-theme tints, because
 * re-teaching an on-call operator what a colour means is not a re-skin.
 */
export type Tone = 'teal' | 'green' | 'sky' | 'amber' | 'red' | 'gray';

/** Dot colours. Spelled out in full so Tailwind's scanner sees every one of them. */
const DOT_CLASSES: Record<Tone, string> = {
  teal: 'bg-primary',
  green: 'bg-emerald-500',
  sky: 'bg-sky-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  gray: 'bg-gray-400',
};

/** Tinted badge surfaces, for a state inside a dense table row. */
const BADGE_CLASSES: Record<Tone, string> = {
  teal: 'bg-primary-50 text-primary-800 ring-primary-100',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  sky: 'bg-sky-50 text-sky-700 ring-sky-100',
  amber: 'bg-amber-50 text-amber-800 ring-amber-100',
  red: 'bg-red-50 text-red-700 ring-red-100',
  gray: 'bg-gray-100 text-gray-600 ring-gray-200',
};

/** Numeric emphasis, for a counter that has to read as good or bad at a glance. */
export const TONE_TEXT: Record<Tone, string> = {
  teal: 'text-primary-700',
  green: 'text-emerald-600',
  sky: 'text-sky-600',
  amber: 'text-amber-600',
  red: 'text-red-600',
  gray: 'text-gray-500',
};

/**
 * A status pill: the landing page hero's trust-chip recipe with a coloured dot in place of the
 * check mark, identical to `apps/portal`'s. The dot is decorative — the text carries the meaning,
 * so a colour-blind reader loses nothing.
 */
export function Pill({
  tone,
  children,
}: {
  readonly tone: Tone;
  readonly children: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm">
      <span
        aria-hidden="true"
        className={`h-2 w-2 rounded-full ${DOT_CLASSES[tone]}`}
      />
      {children}
    </span>
  );
}

/**
 * A tinted badge. Denser than {@link Pill} — this is the one that goes in a table cell.
 *
 * @param title - Optional hover text. The support queue puts the RAW enum token here, so a
 *   translated status badge still hands an operator the value they grep the database with.
 */
export function Badge({
  tone,
  title,
  children,
}: {
  readonly tone: Tone;
  readonly title?: string;
  readonly children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${BADGE_CLASSES[tone]}`}
    >
      {children}
    </span>
  );
}

/** The tone for a BullMQ job state. Unknown states fall back to neutral rather than throwing. */
export function jobStateTone(state: string): Tone {
  switch (state) {
    case 'failed':
      return 'red';
    case 'active':
      return 'green';
    case 'waiting':
      return 'sky';
    case 'delayed':
      return 'amber';
    default:
      return 'gray';
  }
}
